export type DateFilterType = 
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisMonth'
  | 'lastWeek'
  | 'lastMonth'
  | 'customDays'
  | 'tomorrow'
  | 'nextWeek'
  | 'nextMonth'
  | 'nextCustomDays'
  | 'specificDate'
  | 'dateRange';

export interface DateFilterResult {
  after?: string;
  before?: string;
}

export interface DateFilterOptions {
  type: DateFilterType;
  customDays?: number;
  nextCustomDays?: number;
  specificDate?: string;
  dateRange?: {
    from: string;
    to: string;
  };
}

/**
 * Convierte una fecha a string con hora 00:00:00 (para 'after')
 */
export const formatDateAsAfter = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00`;
};

/**
 * Convierte una fecha a string con hora 23:59:59 (para 'before')
 */
export const formatDateAsBefore = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T23:59:59`;
};

/**
 * Calcula las fechas 'after' y 'before' basándose en el tipo de filtro
 */
export const calculateDateFilter = (options: DateFilterOptions): DateFilterResult => {
  const { type, customDays, nextCustomDays, specificDate, dateRange } = options;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (type) {
    case 'today':
      return {
        after: formatDateAsAfter(today),
        before: formatDateAsBefore(today),
      };

    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return {
        after: formatDateAsAfter(yesterday),
        before: formatDateAsBefore(yesterday),
      };

    case 'thisWeek':
      const currentDay = today.getDay();
      const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1;
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - daysFromMonday);
      
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6); // Domingo de la semana actual
      
      return {
        after: formatDateAsAfter(thisWeekStart),
        before: formatDateAsBefore(thisWeekEnd),
      };

    case 'thisMonth':
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        after: formatDateAsAfter(thisMonthStart),
        before: formatDateAsBefore(today),
      };

    case 'lastWeek':
      const lastWeekEnd = new Date(today);
      const currentDayForLastWeek = today.getDay();
      const daysFromMondayForLastWeek = currentDayForLastWeek === 0 ? 6 : currentDayForLastWeek - 1;
      lastWeekEnd.setDate(today.getDate() - daysFromMondayForLastWeek - 1);
      
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
      
      return {
        after: formatDateAsAfter(lastWeekStart),
        before: formatDateAsBefore(lastWeekEnd),
      };

    case 'lastMonth':
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        after: formatDateAsAfter(lastMonthStart),
        before: formatDateAsBefore(lastMonthEnd),
      };

    case 'customDays':
      if (customDays && customDays > 0) {
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - customDays);
        return {
          after: formatDateAsAfter(startDate),
          before: formatDateAsBefore(today),
        };
      }
      return {};

    case 'tomorrow':
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return {
        after: formatDateAsAfter(tomorrow),
        before: formatDateAsBefore(tomorrow),
      };

    case 'nextWeek':
      const currentDayForNextWeek = today.getDay();
      const daysToNextMonday = currentDayForNextWeek === 0 ? 1 : 8 - currentDayForNextWeek;
      const nextWeekStart = new Date(today);
      nextWeekStart.setDate(today.getDate() + daysToNextMonday);
      
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
      
      return {
        after: formatDateAsAfter(nextWeekStart),
        before: formatDateAsBefore(nextWeekEnd),
      };

    case 'nextMonth':
      const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextMonthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return {
        after: formatDateAsAfter(nextMonthStart),
        before: formatDateAsBefore(nextMonthEnd),
      };

    case 'nextCustomDays':
      if (nextCustomDays && nextCustomDays > 0) {
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + nextCustomDays);
        return {
          after: formatDateAsAfter(today),
          before: formatDateAsBefore(endDate),
        };
      }
      return {};

    case 'specificDate':
      if (specificDate) {
        // Construir strings directamente sin crear objetos Date para evitar timezone issues
        return {
          after: `${specificDate}T00:00:00`,
          before: `${specificDate}T23:59:59`,
        };
      }
      return {};

    case 'dateRange':
      if (dateRange?.from && dateRange?.to) {
        // Construir strings directamente sin crear objetos Date para evitar timezone issues
        return {
          after: `${dateRange.from}T00:00:00`,
          before: `${dateRange.to}T23:59:59`,
        };
      }
      return {};

    default:
      return {};
  }
};

/**
 * Convierte parámetros de URL legacy a los nuevos filtros de fecha
 */
export const parseLegacyDateFilters = (searchParams: URLSearchParams): {
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
} => {
  const result: {
    createdAfter?: string;
    createdBefore?: string;
    updatedAfter?: string;
    updatedBefore?: string;
  } = {};

  // Procesar filtros de fecha de creación
  const createdDateFilter = searchParams.get('createdDateFilter');
  if (createdDateFilter) {
    const createdOptions: DateFilterOptions = {
      type: createdDateFilter as DateFilterType,
      customDays: parseInt(searchParams.get('createdCustomDays') || '0') || undefined,
      specificDate: searchParams.get('createdSpecificDate') || undefined,
      dateRange: {
        from: searchParams.get('createdDateFrom') || '',
        to: searchParams.get('createdDateTo') || '',
      },
    };
    
    const createdResult = calculateDateFilter(createdOptions);
    if (createdResult.after) result.createdAfter = createdResult.after;
    if (createdResult.before) result.createdBefore = createdResult.before;
  }

  // Procesar filtros de fecha de actualización
  const updatedDateFilter = searchParams.get('updatedDateFilter');
  if (updatedDateFilter) {
    const updatedOptions: DateFilterOptions = {
      type: updatedDateFilter as DateFilterType,
      customDays: parseInt(searchParams.get('updatedCustomDays') || '0') || undefined,
      specificDate: searchParams.get('updatedSpecificDate') || undefined,
      dateRange: {
        from: searchParams.get('updatedDateFrom') || '',
        to: searchParams.get('updatedDateTo') || '',
      },
    };
    
    const updatedResult = calculateDateFilter(updatedOptions);
    if (updatedResult.after) result.updatedAfter = updatedResult.after;
    if (updatedResult.before) result.updatedBefore = updatedResult.before;
  }

  return result;
};

/**
 * Convierte filtros de fecha directos de URL a objeto
 */
export const parseDirectDateFilters = (searchParams: URLSearchParams): {
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
} => {
  return {
    createdAfter: searchParams.get('createdAfter') || undefined,
    createdBefore: searchParams.get('createdBefore') || undefined,
    updatedAfter: searchParams.get('updatedAfter') || undefined,
    updatedBefore: searchParams.get('updatedBefore') || undefined,
  };
}; 